import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { getUserCfdTier } from '../constants/tradingTiers';

interface UserLeverageSettings {
  maxForex: number;
  maxCommodities: number;
  maxStocks: number;
  maxFutures: number;
  minForex: number;
  minCommodities: number;
  minStocks: number;
  minFutures: number;
}

export const useUserLeverage = (userId: string | undefined, portfolioValue: number): UserLeverageSettings => {
  const [leverageSettings, setLeverageSettings] = useState<UserLeverageSettings>(() => {
    const defaultTier = getUserCfdTier(portfolioValue);
    return {
      maxForex: defaultTier.maxForex,
      maxCommodities: defaultTier.maxCommodities,
      maxStocks: defaultTier.maxStocks,
      maxFutures: 125,
      minForex: 1,
      minCommodities: 1,
      minStocks: 1,
      minFutures: 1,
    };
  });

  useEffect(() => {
    const fetchCustomLeverage = async () => {
      if (!userId) {
        const defaultTier = getUserCfdTier(portfolioValue);
        setLeverageSettings({
          maxForex: defaultTier.maxForex,
          maxCommodities: defaultTier.maxCommodities,
          maxStocks: defaultTier.maxStocks,
          minForex: 1,
          minCommodities: 1,
          minStocks: 1,
        });
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('max_leverage_forex, max_leverage_commodities, max_leverage_stocks, max_leverage_futures, min_leverage_forex, min_leverage_commodities, min_leverage_stocks, min_leverage_futures')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          const defaultTier = getUserCfdTier(portfolioValue);
          setLeverageSettings({
            maxForex: defaultTier.maxForex,
            maxCommodities: defaultTier.maxCommodities,
            maxStocks: defaultTier.maxStocks,
            minForex: 1,
            minCommodities: 1,
            minStocks: 1,
          });
          return;
        }

        const defaultTier = getUserCfdTier(portfolioValue);

        setLeverageSettings({
          maxForex: data?.max_leverage_forex ?? defaultTier.maxForex,
          maxCommodities: data?.max_leverage_commodities ?? defaultTier.maxCommodities,
          maxStocks: data?.max_leverage_stocks ?? defaultTier.maxStocks,
          maxFutures: data?.max_leverage_futures ?? 125,
          minForex: data?.min_leverage_forex ?? 1,
          minCommodities: data?.min_leverage_commodities ?? 1,
          minStocks: data?.min_leverage_stocks ?? 1,
          minFutures: data?.min_leverage_futures ?? 1,
        });
      } catch {
        const defaultTier = getUserCfdTier(portfolioValue);
        setLeverageSettings({
          maxForex: defaultTier.maxForex,
          maxCommodities: defaultTier.maxCommodities,
          maxStocks: defaultTier.maxStocks,
          minForex: 1,
          minCommodities: 1,
          minStocks: 1,
        });
      }
    };

    fetchCustomLeverage();
  }, [userId, portfolioValue]);

  return leverageSettings;
};
